import cn from 'classnames';
import type { PropsWithChildren} from 'react';
import { lazy, useEffect, useRef } from 'react';
import type { Callable, PropsWithCall, UserComponent } from 'react-call';
import { createAutomountingCallable } from '../utils/createLazyCallable';
import preventDefault from '../utils/preventDefault';
import Icon from './Icon';
import * as style from './Modal.module.css';

export interface ModalOptions {
    /**
     * The size of the modal.
     *
     * Default is 'small'.
     */
    size?: 'small' | 'medium' | 'large';
    /**
     * The delay in milliseconds before the modal is unmounted after it is closed. Used to allow the closing animation to complete.
     *
     * Default is 300ms
     */
    unmountingDelay?: number;

    /**
     * Set to true to make the modal uncloseable by the user, forcing the user to interact with the modal
     */
    uncloseable?: boolean;
}

export default function createLazyModal<Props = void, Response = void>(
    moduleImport: () => Promise<{ default: UserComponent<Props, Response | void, unknown> }>,
    { size = 'small', unmountingDelay = 300, uncloseable }: ModalOptions = {},
): Pick<Callable<Props, Response | void, unknown>, 'call' | 'upsert' | 'end' | 'update'> {
    const ModalContent = lazy(moduleImport);

    const modal = (props: PropsWithCall<Props, Response | void, unknown>) => (
        <Modal size={size} onClose={uncloseable ? undefined : props.call.end} isOpen={!props.call.ended}>
            <ModalContent {...props} />
        </Modal>
    );

    return createAutomountingCallable(modal, unmountingDelay);
}

interface Props {
    isOpen: boolean;
    onClose?: () => void;
    size?: 'small' | 'medium' | 'large';
}

function Modal({ isOpen, size = 'small', onClose, children }: PropsWithChildren<Props>) {
    const dialog = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        dialog.current?.showModal();

        if (!onClose) {
            const preventESC = (event: KeyboardEvent) => {
                if (event.key === 'Escape' && isOpen) {
                    event.preventDefault();
                }
            };

            document.addEventListener('keydown', preventESC);

            return () => {
                document.removeEventListener('keydown', preventESC);
            };
        }
    }, [isOpen, onClose, dialog.current]);

    return (
        <dialog
            ref={dialog}
            className={cn(style.dialog, style[size])}
            onClose={() => onClose?.()}
            onCancel={preventDefault(() => onClose?.())}
            onClick={(e) => e.target === e.currentTarget && e.currentTarget.close()}
        >
            <div className={style.dialogContent}>
                {!!onClose && (
                    <button
                        className={style.dialogClose}
                        onClick={(e) => {
                            e.currentTarget.closest('dialog')?.close();
                        }}
                        aria-label="Lukk"
                    >
                        <Icon className="icon-close" />
                    </button>
                )}
                {children}
            </div>
        </dialog>
    );
}
